import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "absolute z-50 flex flex-col p-4 mt-2 space-y-4 bg-white border rounded-lg shadow-lg"
}
const _hoisted_2 = { class: "flex gap-6" }
const _hoisted_3 = { class: "mb-2 font-semibold text-center" }
const _hoisted_4 = { class: "grid grid-cols-7 gap-1 mb-1 text-xs text-center text-gray-600" }
const _hoisted_5 = { class: "grid grid-cols-7 gap-1" }
const _hoisted_6 = ["onClick", "onMouseenter"]
const _hoisted_7 = { class: "flex justify-between pt-4 mt-4 border-t" }

import { ref, computed } from 'vue'
import dayjs, { Dayjs } from 'dayjs'
import { onClickOutside } from '@vueuse/core'


export default /*#__PURE__*/_defineComponent({
  __name: 'DateRangePickerTag',
  props: {
  placeholder: { type: String, default: 'Choisir une plage de dates' },
  value: { type: Object, default: () => ({ start: null, end: null }) },
},
  emits: ['model'],
  setup(__props, { emit: __emit }) {

const emit = __emit
const props = __props

const calendarRef = ref(null)
const popoverOpen = ref(false)

onClickOutside(calendarRef, () => {
  popoverOpen.value = false
})

// Dates de sélection
const startDate = ref<Dayjs | null>(props.value.start ? dayjs(props.value.start) : null)
const endDate = ref<Dayjs | null>(props.value.end ? dayjs(props.value.end) : null)
const hoverDate = ref<Dayjs | null>(null)
const selectingStart = ref(true)

// Mois affichés
const currentMonth = ref(dayjs().startOf('month'))
const nextMonth = computed(() => currentMonth.value.add(1, 'month'))

// Libellé sélectionné
const formattedLabel = computed(() => {
  if (startDate.value && endDate.value) {
    return `${startDate.value.format('DD/MM/YYYY')} - ${endDate.value.format('DD/MM/YYYY')}`
  } else if (startDate.value) {
    return `${startDate.value.format('DD/MM/YYYY')} - ...`
  }
  return props.placeholder
})

// Génère les jours d'un mois
function generateMonthDays(base: Dayjs) {
  const start = base.startOf('month').startOf('week')
  const end = base.endOf('month').endOf('week')
  const days = []
  let day = start

  while (day.isBefore(end)) {
    days.push(day)
    day = day.add(1, 'day')
  }

  return days
}

// Sélection d’un jour
function selectDate(date: Dayjs) {
  if (selectingStart.value) {
    startDate.value = date
    endDate.value = null
    selectingStart.value = false
  } else {
    if (startDate.value && date.isBefore(startDate.value, 'day')) {
      startDate.value = date
      endDate.value = null
      selectingStart.value = false
    } else {
      endDate.value = date
      selectingStart.value = true
      popoverOpen.value = false
      emit('model', {
        start: startDate?.value.toDate(),
        end: date.toDate()
      })
    }
  }
}

// Vérifie si une date est dans la plage sélectionnée
function isInRange(date: Dayjs) {
  return (
    startDate.value &&
    endDate.value &&
    date.isAfter(startDate.value, 'day') &&
    date.isBefore(endDate.value, 'day')
  )
}

function isStart(date: Dayjs) {
  return startDate.value && date.isSame(startDate.value, 'day')
}

function isEnd(date: Dayjs) {
  return endDate.value && date.isSame(endDate.value, 'day')
}

// Navigation mois
function next() {
  currentMonth.value = currentMonth.value.add(1, 'month')
}
function prev() {
  currentMonth.value = currentMonth.value.subtract(1, 'month')
}

// Réinitialisation
function reset() {
  startDate.value = null
  endDate.value = null
  selectingStart.value = true
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "relative w-max",
    ref_key: "calendarRef",
    ref: calendarRef
  }, [
    _createElementVNode("div", {
      class: "px-4 py-2 text-gray-800 bg-white border rounded cursor-pointer",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (popoverOpen.value = !popoverOpen.value))
    }, _toDisplayString(formattedLabel.value), 1),
    (popoverOpen.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", { class: "flex items-center justify-between mb-2" }, [
            _createElementVNode("button", {
              onClick: prev,
              class: "text-sm text-blue-500"
            }, "< Précédent"),
            _createElementVNode("button", {
              onClick: next,
              class: "text-sm text-blue-500"
            }, "Suivant >")
          ]),
          _createElementVNode("div", _hoisted_2, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList([currentMonth.value, nextMonth.value], (month) => {
              return (_openBlock(), _createElementBlock("div", {
                key: month.toString()
              }, [
                _createElementVNode("div", _hoisted_3, _toDisplayString(month.format('MMMM YYYY')), 1),
                _createElementVNode("div", _hoisted_4, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['D', 'L', 'M', 'M', 'J', 'V', 'S'], (d) => {
                    return _createElementVNode("div", { key: d }, _toDisplayString(d), 1)
                  }), 64))
                ]),
                _createElementVNode("div", _hoisted_5, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(generateMonthDays(month), (date) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: date.toString(),
                      class: _normalizeClass(["flex items-center justify-center w-8 h-8 transition-all rounded-full cursor-pointer", {
                'bg-blue-500 text-white': isStart(date) || isEnd(date),
                'bg-blue-100 text-blue-700': isInRange(date),
                'hover:bg-gray-200': !isStart(date) && !isEnd(date),
                'text-gray-400': !date.isSame(month, 'month')
              }]),
                      onClick: ($event: any) => (selectDate(date)),
                      onMouseenter: ($event: any) => (hoverDate.value = date),
                      onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (hoverDate.value = null))
                    }, _toDisplayString(date.date()), 43, _hoisted_6))
                  }), 128))
                ])
              ]))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("button", {
              class: "text-sm text-red-500",
              onClick: reset
            }, "Réinitialiser"),
            _createElementVNode("button", {
              class: "text-sm font-semibold text-green-600",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (popoverOpen.value = false))
            }, " Valider ")
          ])
        ]))
      : _createCommentVNode("", true)
  ], 512))
}
}

})